// ============================================================
// ByOurConsent.com -- Constitutional Notice Generator
// /premium/notices
// Premium member tool -- Paperwork as a Weapon Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming','Washington D.C.'
];

const RESPONDENT_TYPES = [
    { id: 'school_board',    label: 'School board / School district' },
    { id: 'code_enforce',    label: 'Code enforcement / Building inspector' },
    { id: 'local_official',  label: 'Local elected official (mayor, council member, commissioner)' },
    { id: 'police',          label: 'Police department / Sheriff' },
    { id: 'state_agency',    label: 'State agency or department' },
    { id: 'federal_agency',  label: 'Federal agency or department' },
    { id: 'hoa',             label: 'HOA / Property association' },
    { id: 'employer',        label: 'Employer (government or public institution)' },
    { id: 'other',           label: 'Other government body or official' },
];

const NOTICE_TYPES = [
    {
        id:    'non_consent',
        label: 'Notice of Non-Consent',
        desc:  'Formally declare that you do not consent to a policy, mandate, or action being imposed on you.'
    },
    {
        id:    'constitutional_violation',
        label: 'Notice of Constitutional Violation',
        desc:  'Put an official on formal notice that their action violates a specific constitutional provision.'
    },
    {
        id:    'administrative_grievance',
        label: 'Administrative Grievance',
        desc:  'File a formal grievance through official channels demanding review of an action or decision.'
    },
    {
        id:    'cease_desist',
        label: 'Cease and Desist — Government Overreach',
        desc:  'Demand that a government body or official immediately stop an unconstitutional or unlawful action.'
    },
    {
        id:    'public_comment',
        label: 'Formal Public Comment',
        desc:  'Submit a legally formatted comment for the official public record on a proposed rule, policy, or action.'
    },
];

const CONSTITUTIONAL_ISSUES_FEDERAL = [
    { id: 'first',      label: '1st Amendment — Free speech, religion, assembly, press' },
    { id: 'second',     label: '2nd Amendment — Right to bear arms' },
    { id: 'fourth',     label: '4th Amendment — Unreasonable search and seizure' },
    { id: 'fifth',      label: '5th Amendment — Due process, self-incrimination, takings' },
    { id: 'sixth',      label: '6th Amendment — Right to counsel, fair trial' },
    { id: 'tenth',      label: '10th Amendment — States\' rights / federal overreach' },
    { id: 'fourteenth', label: '14th Amendment — Equal protection, due process' },
    { id: 'statutory',  label: 'Federal statutory / regulatory violation' },
    { id: 'other',      label: 'Other constitutional issue' },
];

const CONSTITUTIONAL_ISSUES_LOCAL = [
    { id: 'state_const', label: 'State constitution violation' },
    { id: 'due_process', label: 'Due process violation' },
    { id: 'equal_prot',  label: 'Equal protection / discrimination' },
    { id: 'free_speech', label: 'Free speech / expression' },
    { id: 'exceed_auth', label: 'Exceeded statutory authority' },
    { id: 'no_process',  label: 'Failed to follow required process / procedure' },
    { id: 'open_meet',   label: 'Open meetings / sunshine law violation' },
    { id: 'pub_record',  label: 'Public records violation' },
    { id: 'reg_violation', label: 'Local ordinance or regulation violation' },
    { id: 'fourteenth',  label: '14th Amendment — Equal protection, due process' },
    { id: 'first',       label: '1st Amendment — Free speech, religion, assembly' },
    { id: 'other',       label: 'Other legal issue' },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

const btnPrimary = {
    background: '#8b1a1a', color: '#f5f0e8', border: 'none',
    padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif',
    letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer'
};

const btnSecondary = {
    background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa',
    padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif',
    borderRadius: '3px', cursor: 'pointer'
};

// ============================================================
// Main component
// ============================================================
const NoticeGeneratorPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        noticeType:        '',
        respondentType:    '',
        respondentName:    '',
        state:             '',
        constitutionalIssues: [],
        conduct:           '',
        impact:            '',
        priorAttempts:     '',
        demands:           '',
        addressee:         '',
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const toggleIssue = (id) => {
        setForm(p => ({
            ...p,
            constitutionalIssues: p.constitutionalIssues.includes(id)
                ? p.constitutionalIssues.filter(i => i !== id)
                : [...p.constitutionalIssues, id]
        }));
    };

    const selectedNotice     = NOTICE_TYPES.find(n => n.id === form.noticeType);
    const selectedRespondent = RESPONDENT_TYPES.find(r => r.id === form.respondentType);
    const isFederal          = form.respondentType === 'federal_agency';
    const ISSUES_LIST        = isFederal ? CONSTITUTIONAL_ISSUES_FEDERAL : CONSTITUTIONAL_ISSUES_LOCAL;

    // ============================================================
    // Generate notice via backend
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const issues = [...CONSTITUTIONAL_ISSUES_FEDERAL, ...CONSTITUTIONAL_ISSUES_LOCAL]
            .filter(i => form.constitutionalIssues.includes(i.id))
            .map(i => i.label)
            .join(', ');

        const prompt = `You are an expert in constitutional law and civil rights. Generate a complete, professionally formatted ${selectedNotice?.label} directed at a government official or body.

NOTICE DETAILS:
- Notice type: ${selectedNotice?.label}
- State: ${form.state}
- Respondent (who is being noticed): ${form.respondentName || selectedRespondent?.label}
- Addressed to: ${form.addressee || 'The responsible official/board'}
- Constitutional issues: ${issues || 'General constitutional overreach'}

SITUATION:
- Conduct being challenged: ${form.conduct}
- Impact on the individual/community: ${form.impact || 'Not specified'}
- Prior attempts to resolve: ${form.priorAttempts || 'None specified'}
- Specific demands: ${form.demands || 'Immediate cessation of the challenged conduct'}

REQUIREMENTS:
1. Open with a clear statement of purpose and the constitutional basis
2. Cite specific constitutional provisions (amendments, articles, sections) that apply
3. For state issues, also cite relevant ${form.state} state constitutional provisions where applicable
4. Clearly describe the conduct being challenged in formal legal language
5. State the legal standard being violated
6. Include relevant case law or legal precedent where applicable
7. Make specific, clear demands with a reasonable response deadline (typically 10-15 business days)
8. State the consequences of non-response or non-compliance (escalation to oversight bodies, legal action, public record)
9. Use formal, professional legal language throughout
10. Format as a proper formal notice/letter

FORMAT EXACTLY LIKE THIS:
[YOUR NAME]
[YOUR ADDRESS]
[YOUR CITY, STATE ZIP]
[YOUR EMAIL]
[YOUR PHONE]

[DATE]

${form.addressee || '[OFFICIAL NAME AND TITLE]'}
${form.respondentName || '[AGENCY/BOARD NAME]'}
[AGENCY ADDRESS]
[AGENCY CITY, STATE ZIP]

RE: ${selectedNotice?.label?.toUpperCase()}

Dear ${form.addressee || '[Official Name]'}:

[Body of notice with all required elements]

Respectfully submitted,

[YOUR NAME]
[YOUR SIGNATURE]

---
NOTICE OF RECORD: This notice is being submitted as a formal legal document and may be filed with appropriate oversight bodies, entered into the public record, and used in any subsequent legal proceedings.

Generate the complete notice now. Be thorough, cite specific constitutional provisions, and maintain formal legal tone throughout.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 2000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({ notice: text, noticeType: selectedNotice?.label, respondent: form.respondentName || selectedRespondent?.label });
            setStep(3);
        } catch (err) {
            setError('Failed to generate notice. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download as RTF (opens in Word)
    // ============================================================
    const downloadWord = () => {
        downloadReportAsWord(
            result.notice,
            result.noticeType?.replace(/[^a-z0-9]/gi, '-').slice(0,40) || 'Notice'
        );
    };

    // ============================================================
    // Reset
    // ============================================================
    const reset = () => {
        setStep(1);
        setResult(null);
        setError('');
        setForm({
            noticeType: '', respondentType: '', respondentName: '',
            state: '', constitutionalIssues: [], conduct: '',
            impact: '', priorAttempts: '', demands: '', addressee: ''
        });
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet>
                <title>Constitutional Notice Generator — By Our Consent</title>
            </Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Paperwork as a weapon
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        Constitutional Notice Generator
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Formally put government officials on notice when they overstep.
                        Professionally formatted notices that cite the correct constitutional provisions
                        and create a legal paper trail.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Notice type + respondent ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — What type of notice and who is it directed at?
                        </div>

                        {/* Notice type */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Notice type</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {NOTICE_TYPES.map(n => (
                                    <label key={n.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: form.noticeType === n.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.noticeType === n.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.noticeType === n.id ? 600 : 400 }}>{n.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '2px' }}>{n.desc}</div>
                                        </div>
                                        <input type="radio" name="noticeType" value={n.id}
                                            checked={form.noticeType === n.id}
                                            onChange={() => set('noticeType', n.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Respondent type */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Who is this directed at?</label>
                            <select style={iStyle} value={form.respondentType} onChange={e => set('respondentType', e.target.value)}>
                                <option value="">Select...</option>
                                {RESPONDENT_TYPES.map(r => (
                                    <option key={r.id} value={r.id}>{r.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Respondent name */}
                        {form.respondentType && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>Specific name of agency, board, or body</label>
                                <input style={iStyle} type="text"
                                    placeholder="e.g. Richmond City School Board, Norfolk Code Enforcement Division..."
                                    value={form.respondentName}
                                    onChange={e => set('respondentName', e.target.value)} />
                            </div>
                        )}

                        {/* State */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>State</label>
                            <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                <option value="">Select state...</option>
                                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>

                        <button style={{ ...btnPrimary, opacity: (!form.noticeType || !form.respondentType || !form.state) ? 0.5 : 1 }}
                            disabled={!form.noticeType || !form.respondentType || !form.state}
                            onClick={() => setStep(2)}>
                            Next: Describe the situation →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Details ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Describe the situation
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Conduct being challenged — be specific and factual</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Describe exactly what the official or body is doing or has done. Dates, specific actions,
                                    and direct quotes from any written policies or orders are helpful.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "On March 15, 2026, the Riverside School Board voted to require all students to
                                    participate in a mandatory political advocacy program during school hours without parental consent or opt-out option."
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Specific demands — state clearly what you want them to do</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Be direct about what action you are demanding. Vague demands are easier to ignore.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Immediately suspend the mandatory program, provide written confirmation within 10 days,
                                    and schedule a public hearing before any reinstatement."
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Addressee — name the specific person when possible</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Dr. Jane Smith, Superintendent, Riverside Unified School District"
                                </div>
                            </div>
                        </details>

                        {/* Summary of step 1 */}
                        <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060' }}>
                            <strong style={{ color: '#1e2a3a' }}>{selectedNotice?.label}</strong> directed at <strong style={{ color: '#1e2a3a' }}>{form.respondentName || selectedRespondent?.label}</strong> · {form.state}
                        </div>

                        {/* Constitutional issues */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>
                                {isFederal ? 'Constitutional issues involved' : 'Legal basis for your notice'} (select all that apply)
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {ISSUES_LIST.map(issue => (
                                    <label key={issue.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.constitutionalIssues.includes(issue.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.constitutionalIssues.includes(issue.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{issue.label}</div>
                                        </div>
                                        <input type="checkbox"
                                            checked={form.constitutionalIssues.includes(issue.id)}
                                            onChange={() => toggleIssue(issue.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Conduct */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Conduct being challenged *</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={4}
                                placeholder="Describe exactly what the official or body is doing or has done. Include dates, specific actions, and any relevant details..."
                                value={form.conduct}
                                onChange={e => set('conduct', e.target.value)} />
                        </div>

                        {/* Impact */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Impact on you or your community (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="How does this conduct affect you, your family, or your community?..."
                                value={form.impact}
                                onChange={e => set('impact', e.target.value)} />
                        </div>

                        {/* Prior attempts */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Prior attempts to resolve (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                                placeholder="Have you already contacted this official or attended a meeting? What happened?..."
                                value={form.priorAttempts}
                                onChange={e => set('priorAttempts', e.target.value)} />
                        </div>

                        {/* Demands */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Specific demands (optional — AI will suggest if left blank)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="What specific action do you demand? e.g. Immediately suspend the policy, provide written confirmation within 10 days..."
                                value={form.demands}
                                onChange={e => set('demands', e.target.value)} />
                        </div>

                        {/* Addressee */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Addressed to (name and title of specific official)</label>
                            <input style={iStyle} type="text"
                                placeholder="e.g. Dr. Jane Smith, Superintendent — or leave blank for general addressing"
                                value={form.addressee}
                                onChange={e => set('addressee', e.target.value)} />
                        </div>

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: !form.conduct || loading ? 0.6 : 1 }}
                                disabled={!form.conduct || loading}
                                onClick={generate}>
                                {loading ? 'Generating your notice...' : 'Generate Notice →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                ⏳ Researching constitutional provisions and drafting your notice...
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Your notice is ready
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>
                                    Review carefully, fill in the placeholders, then send.
                                </div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Word Doc
                            </button>
                        </div>

                        {/* Instructions */}
                        <div style={{ background: '#e8f5e0', border: '0.5px solid #a8d0a0', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11', lineHeight: 1.6 }}>
                            <strong>Before sending:</strong> Replace all [YOUR NAME], [YOUR ADDRESS], [DATE], and any other
                            placeholders with your actual information. Consider sending via certified mail to create
                            a legal record of delivery.
                        </div>

                        {/* Notice preview */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', whiteSpace: 'pre-wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.notice}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Word Doc
                            </button>
                            <button onClick={reset} style={btnSecondary}>
                                Generate another notice
                            </button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This notice is generated as a starting point. Review it carefully before sending.
                            By Our Consent is not a law firm and this is not legal advice.
                            For high-stakes situations, consult a qualified attorney before sending.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default NoticeGeneratorPage;
